/**
 * Split View Hook
 *
 * Manages state for the split-screen dual chat feature.
 * Allows users to view and interact with two threads simultaneously.
 */

"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useCallback } from "react";
import type { SplitViewState, MessageType } from "@/types/splitView";
interface UseSplitViewResult {
  splitView: SplitViewState;
  activateSplitView: (leftThreadId: string, rightThreadId: string | null) => void;
  deactivateSplitView: () => void;
  setSplitRatio: (ratio: number) => void;
  swapPanels: () => void;
  setLeftThread: (threadId: string) => void;
  setRightThread: (threadId: string) => void;
  toggleCrossChat: () => void;
  setLeftPanelName: (name: string) => void;
  setRightPanelName: (name: string) => void;
  setMessageType: (type: MessageType) => void;
}
export function useSplitView() {
  const $ = _c(13);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      isActive: false,
      leftThreadId: null,
      rightThreadId: null,
      splitRatio: 50,
      crossChatEnabled: false,
      leftPanelName: "Main Chat",
      rightPanelName: "Assistant",
      messageType: "instruction"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [splitView, setSplitView] = useState(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = (leftThreadId, rightThreadId) => {
      setSplitView({
        isActive: true,
        leftThreadId,
        rightThreadId,
        splitRatio: 50,
        crossChatEnabled: false,
        leftPanelName: "Main Chat",
        rightPanelName: "Assistant",
        messageType: "instruction"
      });
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const activateSplitView = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setSplitView({
        isActive: false,
        leftThreadId: null,
        rightThreadId: null,
        splitRatio: 50,
        crossChatEnabled: false,
        leftPanelName: "Main Chat",
        rightPanelName: "Assistant",
        messageType: "instruction"
      });
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const deactivateSplitView = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ratio => {
      setSplitView(prev => ({
        ...prev,
        splitRatio: Math.max(20, Math.min(80, ratio))
      }));
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const setSplitRatio = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setSplitView(_temp);
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const swapPanels = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = threadId => {
      setSplitView(prev_1 => ({
        ...prev_1,
        leftThreadId: threadId
      }));
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const setLeftThread = t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = threadId_0 => {
      setSplitView(prev_2 => ({
        ...prev_2,
        rightThreadId: threadId_0
      }));
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const setRightThread = t6;
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setSplitView(_temp2);
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const toggleCrossChat = t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = name => {
      setSplitView(prev_4 => ({
        ...prev_4,
        leftPanelName: name
      }));
    };
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const setLeftPanelName = t8;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = name_0 => {
      setSplitView(prev_5 => ({
        ...prev_5,
        rightPanelName: name_0
      }));
    };
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const setRightPanelName = t9;
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = type => {
      setSplitView(prev_6 => ({
        ...prev_6,
        messageType: type
      }));
    };
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const setMessageType = t10;
  let t11;
  if ($[11] !== splitView) {
    t11 = {
      splitView,
      activateSplitView,
      deactivateSplitView,
      setSplitRatio,
      swapPanels,
      setLeftThread,
      setRightThread,
      toggleCrossChat,
      setLeftPanelName,
      setRightPanelName,
      setMessageType
    };
    $[11] = splitView;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  return t11;
}
function _temp2(prev_3) {
  return {
    ...prev_3,
    crossChatEnabled: !prev_3.crossChatEnabled
  };
}
function _temp(prev_0) {
  return {
    ...prev_0,
    leftThreadId: prev_0.rightThreadId,
    rightThreadId: prev_0.leftThreadId,
    splitRatio: 100 - prev_0.splitRatio
  };
}